import { css } from '@emotion/react';
import { useMutation } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import React from 'react';

import { QuestionMarkIcon } from '~src/icons/question-mark';
import { fontStyles } from '~src/theme/fontStyles';
import { Checkbox } from '~src/ui/checkbox';
import { MultilineEllipsis } from '~src/ui/multiline-ellipsis';

import { DeliveryAddon } from '../../api/api';
import { CategoryExtensionProps } from '../category/category-manager';
import {
  apiInstance,
  useGlobalState,
  useGlobalStateUpdate,
  useSessionState,
} from '../global-state';
import { PriceComponent } from '../price/price';
import { colors } from '../theme/colors';

const sanitizeOptions = {
  ALLOWED_TAGS: ['br', 'b', 'strong'],
};

interface DeliveryAddonLineProps {
  addon: DeliveryAddon;
  categoryId: string;
}

const DeliveryAddonLine: React.FC<DeliveryAddonLineProps> = ({ addon, categoryId }) => {
  const updateFn = useGlobalStateUpdate();
  const globalState = useGlobalState();
  const session = useSessionState();
  const [descriptionVisible, setDescriptionVisible] = React.useState(false);

  const mutationFn = async (selected: boolean) => {
    const { data } = await apiInstance.v4.deliveryAddonStatusPatch(
      globalState.checkout_session_id,
      session.session_id,
      categoryId,
      addon.id,
      { selected }
    );
    return data;
  };

  const mutation = useMutation({
    mutationFn,
    mutationKey: ['delivery-addon'],
    onSuccess: data => {
      updateFn(data);
    },
  });

  /**
   * Note: delivery addons are very rarely used, it is a little profit to
   * struggle with optimistic update here, especially when we not only have to
   * update the checkbox but also the price.
   */
  const selected = addon.value.selected;

  const onChange = (newSelected: boolean) => {
    mutation.mutate(newSelected);
  };

  return (
    <div>
      <div css={styles.wrapper} data-test="delivery-addon-box">
        <Checkbox labelId={addon.id} checked={selected} onChange={() => onChange(!selected)} />
        <label htmlFor={addon.id} css={styles.labelFont}>
          {addon.info.name}
        </label>
        {addon.info.description && (
          <div css={styles.infoIcon} onMouseEnter={() => setDescriptionVisible(true)}>
            <QuestionMarkIcon color={colors.black} />
          </div>
        )}
        <PriceComponent
          data-test="addon-price"
          css={styles.priceFont}
          value={addon.price}
          showPlus
        />
      </div>
      {descriptionVisible && (
        <div css={styles.description}>
          <MultilineEllipsis>
            {parse(DOMPurify.sanitize(addon.info.description, sanitizeOptions))}
          </MultilineEllipsis>
        </div>
      )}
    </div>
  );
};

export const DeliveryAddonExtension: React.FC<CategoryExtensionProps> = ({
  category,
  selected,
}) => {
  if (!selected || !category.delivery_addons) {
    return null;
  }

  return (
    <>
      {category.delivery_addons.map(addon => (
        <DeliveryAddonLine key={addon.id} addon={addon} categoryId={category.id} />
      ))}
    </>
  );
};

const styles = {
  wrapper: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  }),
  infoIcon: {
    marginLeft: '4px',
    height: '18px',
  },
  labelFont: {
    color: colors.black,
    fontVariationSettings: '"wght" 500',
    ...fontStyles.px12_18,
    padding: '1px 0',
    cursor: 'pointer',
    marginLeft: '8px',
  },
  priceFont: {
    color: colors.neutral35,
    ...fontStyles.px12_16,
    padding: '1px 0',
    marginLeft: '8px',
  },
  description: {
    marginTop: '8px',
  },
};
